import { MutationLevelEnum, IMutation } from "./types/mutation";
import { findDuplicates } from "../../utils/tools";
import { allAminoAcids, allNucleotides } from "@loschmidt/commons";
import { useCallback } from "react";
import { useInputContext } from "./context/input-context";

enum EntryState {
  INIT, PARSED, CHECKED
}

class MutationEntry {

  static regex = /^([A-Z])(\d+)([A-Z])$/;

  state: EntryState = EntryState.INIT;
  msg: string | null = null;
  ok: boolean = true;
  idx: number;
  dataRaw: string;
  data: string;

  wildTypeAA: string = "";
  position: number = -1;
  mutatedAA: string = "";

  multiple: boolean;  // multiple mutations

  constructor(data: string, idx: number, multiple: boolean) {
    this.dataRaw = data;
    this.data = data.toUpperCase();
    this.idx = idx;
    this.multiple = multiple;
  }

  parse() {
    if (this.state !== EntryState.INIT) {
      throw "Method parse() is available only in state INIT";
    }
    if (!this.ok) {
      throw "Cannot run method parse() as this.ok !== true";
    }
    const m = this.data.match(MutationEntry.regex);
    if (m !== null) {
      this.wildTypeAA = m[1];
      this.position = Number(m[2]);
      this.mutatedAA = m[3];
      this.state = EntryState.PARSED;
    } else {
      this.ok = false;
      this.msg = `Mutation entry is incorrect, use format [wild_type][position][mutated]`;
    }
  }

  // Constraints:
  // - 1 <= position <= sequence.length
  // - sequence[position-1] == wildTypeAA
  // - mutatedAA != wildTypeAA
  // - availableUnits includes mutatedAA
  check(sequence: string, availableUnits: string[]) {
    if (this.state !== EntryState.PARSED) {
      throw "Method check() is available only in state PARSED";
    }
    if (!this.ok) {
      throw "Cannot run method check() as this.ok !== true";
    }
    if (this.position < 1) {
      this.ok = false;
      this.msg = `Position must be at least 1`;
    } else if (this.position > sequence.length) {
      this.ok = false;
      this.msg = `Position must be at most ${sequence.length}, which is the sequence length`;
    } else if (sequence[this.position-1] !== this.wildTypeAA) {
      this.ok = false;
      this.msg = `Amino acid (${this.wildTypeAA}) does not match with the wild type amino acid (${sequence[this.position-1]}) at position ${this.position}. Modify the selection or try using “Sequence input”`;
    } else if (this.wildTypeAA === this.mutatedAA) {
      this.ok = false;
      this.msg = `Mutated amino acid (${this.mutatedAA}) is the same as the wild type amino acid (${this.wildTypeAA})`;
    } else if (!availableUnits.includes(this.mutatedAA)) {
      this.ok = false;
      this.msg = `Mutated amino acid (${this.mutatedAA}) is invalid`;
    } else {
      this.state = EntryState.CHECKED;
    }
  }

  getMsg() {
    if (this.ok) {
      return "";
    }
    return this.multiple ? `Line ${this.idx+1}: ${this.msg}: ${this.dataRaw}` : `${this.msg}.`;
  }
}

const checkMutationsAsAWhole = (mutations: IMutation[], multipleUnits: boolean): string[] => {
  // multiple mutations: two mutated units for one position
  const multipleMutations = mutations.filter(m => m.units.length > 1);
  // duplicate mutations: one mutated unit for one position, but entered multiple times
  const duplicateMutations = multipleMutations.map(m => ({ position: m.position, units: findDuplicates(m.units) }));
  let messages: string[];
  if (multipleUnits) {
    messages = duplicateMutations.map(dm => `Duplicate mutations at position ${dm.position}: ${dm.units.join(', ')}`);
  } else {
    messages = multipleMutations.map(mm => `Multiple mutations at position ${mm.position}: ${mm.units.join(', ')}`);
  }
  return messages;
};

function mutationParser({sequence, level, value, multiplePos, multipleUnits}: {
  sequence: string,
  level: MutationLevelEnum,
  value: string,
  multiplePos: boolean,
  multipleUnits: boolean,
}) {
  // trim value of input / textarea
  value = value.trim();
  // split value into lines
  const lines = value.split("\n").map(l => l.trim()).filter(l => l.length > 0);
  // create MutationEntry objects
  const multiple = multiplePos || multipleUnits;
  const entries = lines.map((line, idx) => new MutationEntry(line, idx, multiple));
  // get available units - all nucleotides or amino acids
  const availableUnits = level === MutationLevelEnum.DNA ? allNucleotides : allAminoAcids;
  // parse entries
  entries.forEach(entry => entry.parse());
  // check entries that are successfully parsed
  entries.filter(entry => entry.ok).forEach(entry => entry.check(sequence, availableUnits));
  // collect error messages of entries
  let messages = entries.filter(entry => !entry.ok).map(entry => entry.getMsg());
  let mutations: IMutation[] = [];
  if (messages.length == 0) {
    // create position-to-units object
    const positionToUnits: {[key: string]: string[]} = entries.reduce((result, entry) => {
      (result[entry.position] = result[entry.position] || []).push(entry.mutatedAA);
      return result;
    }, {});
    // create mutation array
    mutations = Object.entries(positionToUnits).map(
      e => ({ position: Number(e[0]), units: e[1] })
    );
    // sort mutations by position
    mutations.sort((a, b) => a.position - b.position);
    // check multiple and duplicate mutations
    messages = checkMutationsAsAWhole(mutations, multipleUnits);
  }
  return {messages, mutations};
}

export function useMutationParser() {
  const {
    sequence,
    level,
    multiplePos,
    multipleUnits,
  } = useInputContext();
  return useCallback((value: string) => {
    return mutationParser({sequence, level, value, multiplePos, multipleUnits});
  }, [sequence, level, multiplePos, multipleUnits]);
}